import React, { useEffect, useState } from "react";
import { IDENTITIES, HERO_PORTRAIT } from "../lib/siteData";
import { ArrowDownRight } from "lucide-react";

export default function Hero() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % IDENTITIES.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden"
    >
      {/* Decorative vibrant shapes */}
      <div
        aria-hidden
        className="absolute -top-24 -right-24 h-[480px] w-[480px] rounded-full opacity-[0.08] blur-3xl"
        style={{ background: "radial-gradient(circle, #EC4899 0%, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute top-1/2 -left-32 h-[420px] w-[420px] rounded-full opacity-[0.10] blur-3xl"
        style={{ background: "radial-gradient(circle, #D97706 0%, transparent 70%)" }}
      />
      <div className="container-prose grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
        <div className="lg:col-span-7">
          <p className="overline animate-fade-up" data-testid="hero-overline">
            Educator · Trainer · Framework Architect · TESOL Faculty
          </p>

          <h1 className="h1-display mt-6 text-5xl sm:text-7xl lg:text-[5.6rem] text-ink">
            Karamjeet <span className="italic font-light text-saffron">Sodhi</span>
          </h1>

          <div
            className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 body-prose text-lg sm:text-2xl text-ink/85"
            data-testid="hero-identities"
          >
            {IDENTITIES.map((id, i) => (
              <React.Fragment key={id}>
                <span
                  className={`transition-all duration-500 ${
                    idx === i
                      ? "text-saffron italic font-medium"
                      : "text-ink/55"
                  }`}
                  style={{ fontFamily: '"Cormorant Garamond", serif' }}
                >
                  {id}
                </span>
                {i < IDENTITIES.length - 1 && (
                  <span className="text-magenta/60">·</span>
                )}
              </React.Fragment>
            ))}
          </div>

          <p className="mt-10 max-w-xl body-prose text-ink/75 text-base sm:text-lg">
            Two decades bridging classroom reality with world-class research.
            <strong className="font-medium text-ink"> 1,000+ trainers, 10,000+ teachers</strong> and over
            <strong className="font-medium text-ink"> a million students</strong> impacted across India and abroad —
            aligned to NEP 2020 and NCF-SE 2023.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              data-testid="hero-book-cta"
              className="group inline-flex items-center gap-3 px-7 py-4 bg-ink text-ivory text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-forest transition-colors"
            >
              Invite Karamjeet
              <ArrowDownRight
                size={16}
                className="transition-transform group-hover:translate-x-1 group-hover:translate-y-1"
              />
            </a>
            <a
              href="#frameworks"
              data-testid="hero-frameworks-cta"
              className="inline-flex items-center gap-2 px-7 py-4 text-[12px] uppercase tracking-[0.22em] font-medium text-ink border-b border-ink/30 hover:border-forest hover:text-forest transition-colors"
            >
              Explore the Frameworks
            </a>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          {/* Saffron offset block */}
          <div
            aria-hidden
            className="absolute -bottom-4 -right-4 h-24 w-24 sm:h-32 sm:w-32 bg-saffron z-0"
          />
          <div
            aria-hidden
            className="absolute -top-4 -left-4 h-20 w-20 sm:h-24 sm:w-24 border-2 border-magenta z-0"
          />
          <div className="relative aspect-[4/5] w-full overflow-hidden bg-ink/5 z-10">
            <img
              src={HERO_PORTRAIT}
              alt="Karamjeet Sodhi at TESOL25 International Convention & Expo"
              className="w-full h-full object-cover object-top"
              loading="eager"
              data-testid="hero-portrait"
            />
            <div className="absolute inset-0 ring-1 ring-ink/15 pointer-events-none" />
          </div>
          <div className="absolute -bottom-6 -left-6 hidden sm:block bg-ivory border border-ink/15 px-6 py-4 max-w-[280px] shadow-[0_20px_40px_-20px_rgba(30,27,75,0.3)] z-20">
            <p className="overline text-saffron">TESOL · CBSE · NCERT · US State Dept</p>
            <p className="mt-2 text-sm body-prose text-ink/80">
              International Faculty · Master Trainer · Exchange Alumni
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
